import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PropertyCard from "@/components/app/PropertyCard";
import { motion } from "framer-motion";
import { Building2, Coins, TrendingUp, ShieldCheck, Sparkles } from "lucide-react";

export default function Landing() {
  const [props, setProps] = useState([]);

  useEffect(() => {
    api.get("/properties").then(({ data }) => setProps(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="text-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1758193431355-54df41421657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBkdXNrfGVufDB8fHx8MTc3NjQyMDU3M3ww&ixlib=rb-4.1.0&q=85"
            alt="luxury"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050914]/60 via-[#050914]/85 to-[#050914]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
              <Sparkles className="w-3 h-3 text-[#D4AF37]" />
              <span className="text-[11px] font-mono tracking-widest text-[#D4AF37] uppercase">
                Fractional Real Estate · Tokenized
              </span>
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-light tracking-tighter mt-6 max-w-4xl">
              Own a <span className="text-[#D4AF37]">brick</span>
              <br />
              of the world's
              <br />
              most elite addresses.
            </h1>
            <p className="mt-6 text-gray-400 text-base md:text-lg max-w-xl leading-relaxed">
              Buy fractional shares of luxury properties from ₦297,000 per brick. Each brick is a unique
              blockchain-verified token giving you real ownership, rental income, and capital appreciation.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register">
                <Button data-testid="hero-cta-register" className="btn-gold rounded-full h-12 px-8 text-sm">
                  Start investing
                </Button>
              </Link>
              <Link to="/properties">
                <Button data-testid="hero-cta-browse" variant="outline" className="btn-gold-outline rounded-full h-12 px-8 text-sm">
                  Browse properties
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { v: "₦2.8T+", l: "Assets tokenized" },
              { v: "24+", l: "Countries" },
              { v: "7.9%", l: "Avg yield" },
              { v: "12k+", l: "Investors" },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.08 }}
              >
                <div className="font-heading text-3xl md:text-4xl text-white font-light tracking-tight">{s.v}</div>
                <div className="text-xs text-gray-500 uppercase tracking-widest mt-1">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              i: Building2,
              t: "Choose property",
              d: "Browse curated luxury assets across Dubai, London, Miami and beyond. View yields, location, and brick availability.",
            },
            {
              i: Coins,
              t: "Buy bricks",
              d: "Each property is split into bricks. Buy as few as you want. Each brick is a unique BP-ID token with a digital certificate.",
            },
            {
              i: TrendingUp,
              t: "Earn & trade",
              d: "Receive rental income proportional to your bricks, and resell on our secondary marketplace anytime.",
            },
          ].map((s, i) => (
            <motion.div
              key={s.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bp-card rounded-2xl p-8"
            >
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-5">
                <s.i className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="overline mb-2">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-heading text-2xl font-medium text-white">{s.t}</h3>
              <p className="text-gray-400 mt-3 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline">Featured listings</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mt-2">
              Open for investment
            </h2>
          </div>
          <Link to="/properties" className="text-[#D4AF37] text-sm hover:underline hidden md:block">
            View all →
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {props.map((p, i) => (
            <PropertyCard key={p.id} p={p} index={i} />
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 border-t border-white/5">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline">Trust & security</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tighter text-white mt-3">
              Every brick is a <span className="text-[#D4AF37]">token</span>
            </h2>
            <p className="text-gray-400 mt-5 leading-relaxed">
              Ownership is recorded immutably. Each brick carries a unique identifier <span className="font-mono text-[#D4AF37]">BP-&lt;property&gt;-&lt;brick&gt;</span>,
              verifiable through a digital ownership certificate you can view, share or print.
            </p>
            <div className="mt-8 space-y-3">
              {["KYC-verified investor onboarding", "Stripe-secured payments", "Real-time ownership sync"].map((f) => (
                <div key={f} className="flex items-center gap-3 text-sm text-gray-300">
                  <ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> {f}
                </div>
              ))}
            </div>
          </div>
          <div className="bp-card rounded-2xl p-8 relative overflow-hidden">
            <img
              src="https://images.pexels.com/photos/14891569/pexels-photo-14891569.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="token"
              className="w-full h-64 object-cover rounded-xl"
            />
            <div className="mt-5 font-mono text-[#D4AF37] text-sm tracking-widest">BP-A8F29B31-00042</div>
            <div className="text-xs text-gray-500 mt-1">Azure Heights Residences · Brick #42</div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-14 bg-[#050914]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#FDE047] flex items-center justify-center">
                  <span className="font-heading font-bold text-[#050914] text-sm">BP</span>
                </div>
                <div className="font-heading text-white">Blockchain Properties LTD</div>
              </div>
              <p className="text-gray-400 text-sm max-w-md leading-relaxed">
                Fractional real-estate ownership backed by blockchain-verified tokens. Every brick is unique, on-ledger, and yours.
              </p>
            </div>
            <div>
              <div className="overline mb-3">Head office</div>
              <div className="text-gray-400 text-sm leading-relaxed">5 Umuasonye Street,<br/>Orji, Owerri, Imo State</div>
              <div className="overline mb-3 mt-5">Abuja branch</div>
              <div className="text-gray-400 text-sm leading-relaxed">16 Adedoyin Rhodes Vivor Close,<br/>by AfriEmergencies,<br/>Asokoro, FCT</div>
            </div>
            <div>
              <div className="overline mb-3">Contact</div>
              <div className="text-gray-400 text-sm space-y-2">
                <a href="mailto:blockchainpropertiesltd@gmail.com" className="block hover:text-[#D4AF37] break-all">
                  blockchainpropertiesltd@gmail.com
                </a>
                <a href="https://wa.me/2348063535217" target="_blank" rel="noreferrer" className="block hover:text-[#D4AF37]">
                  WhatsApp +234 806 353 5217
                </a>
              </div>
              <div className="overline mb-3 mt-5">Bank</div>
              <div className="text-gray-400 text-xs leading-relaxed">
                <div className="text-white text-sm">Zenith Bank</div>
                <div>Blockchain Properties LTD</div>
                <div className="mt-2">₦ Naira: <span className="font-mono text-[#D4AF37]">1312318687</span></div>
                <div>$ Dollar: <span className="font-mono text-[#D4AF37]">5076055316</span></div>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/5 text-xs text-gray-500 flex justify-between flex-wrap gap-4">
            <div>© 2026 Blockchain Properties Ltd. Fractional ownership made real.</div>
            <div className="flex gap-5">
              <Link to="/login" className="hover:text-white">Login</Link>
              <Link to="/register" className="hover:text-white">Get started</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
